import { UserGroupRole } from './Types/UserGroupRoles';

/**
 * A record of all UserRoles, with each role containing the roles it may change a user's role from and to.
 *
 * Mirrors RoleGrantRules on the server: at or below your own level, in both directions — the person's
 * current level and every level being handed out. It is the reach, not the permission to act; that is
 * ManageUserAccess, checked separately, and neither applies to your own account. Guest accounts are left
 * out although the server would allow them: nobody was offered a role change on a guest before.
 */
export const ChangePermissionsPolicy: Record<UserGroupRole, UserGroupRole[]> = {
	'Super Administrators': [
		'Super Administrators',
		'Admin',
		'Manager',
		'Staff',
		'InvoicedUser',
		'FixedPriceUser',
		'User',
	],
	Admin: ['Admin', 'Manager', 'Staff', 'InvoicedUser', 'FixedPriceUser', 'User'],
	Manager: ['Manager', 'Staff', 'InvoicedUser', 'FixedPriceUser', 'User'],
	Staff: ['Staff', 'InvoicedUser', 'FixedPriceUser', 'User'],
	// Customer roles cannot hold ManageUserAccess, so they reach nobody.
	InvoicedUser: [],
	FixedPriceUser: [],
	User: [],
	Guest: [],
};

/**
 * A record of all UserRoles, with each role containing a list of the roles it is authorized to delete.
 * Mirrors DeactivationPolicy — staff+ can delete users at strictly lower privilege than themselves.
 * The backend re-checks the same matrix and additionally refuses delete if the target has linked records.
 */
export const DeleteUserPolicy: Record<UserGroupRole, UserGroupRole[]> = {
	'Super Administrators': ['Admin', 'Manager', 'Staff', 'InvoicedUser', 'FixedPriceUser', 'User'],
	Admin: ['Manager', 'Staff', 'InvoicedUser', 'FixedPriceUser', 'User'],
	Manager: ['Staff', 'InvoicedUser', 'FixedPriceUser', 'User'],
	Staff: ['InvoicedUser', 'FixedPriceUser', 'User'],
	InvoicedUser: [],
	FixedPriceUser: [],
	User: [],
	Guest: [],
};

/**
 * A record of all UserRoles, with each role containing a list of the roles it is authorized to deactivate.
 */
export const DeactivationPolicy: Record<UserGroupRole, UserGroupRole[]> = {
	'Super Administrators': [
		'Super Administrators',
		'Admin',
		'Manager',
		'Staff',
		'InvoicedUser',
		'FixedPriceUser',
		'User',
	],
	Admin: ['Admin', 'Manager', 'Staff', 'InvoicedUser', 'FixedPriceUser', 'User'],
	Manager: ['Staff', 'InvoicedUser', 'FixedPriceUser', 'User'],
	Staff: ['InvoicedUser', 'FixedPriceUser', 'User'],
	InvoicedUser: [],
	FixedPriceUser: [],
	User: [],
	Guest: [],
};

/**
 * A record of all UserRoles, with each role containing a list of the roles it is authorized to edit account details.
 * Staff+ users cannot edit accounts at or above their own role level (except their own account).
 */
export const EditAccountPolicy: Record<UserGroupRole, UserGroupRole[]> = {
	'Super Administrators': ['Admin', 'Manager', 'Staff', 'InvoicedUser', 'FixedPriceUser', 'User', 'Guest'],
	Admin: ['Manager', 'Staff', 'InvoicedUser', 'FixedPriceUser', 'User', 'Guest'],
	Manager: ['Staff', 'InvoicedUser', 'FixedPriceUser', 'User', 'Guest'],
	Staff: ['InvoicedUser', 'FixedPriceUser', 'User', 'Guest'],
	InvoicedUser: [],
	FixedPriceUser: [],
	User: [],
	Guest: [],
};

/**
 * A record of all UserRoles, with each role containing a list of the roles it is authorized to enable/disable 2FA.
 */
export const ToggleTwoFactorPolicy: Record<UserGroupRole, UserGroupRole[]> = {
	'Super Administrators': [
		'Super Administrators',
		'Admin',
		'Manager',
		'Staff',
		'InvoicedUser',
		'FixedPriceUser',
		'User',
	],
	Admin: ['Admin', 'Manager', 'Staff', 'InvoicedUser', 'FixedPriceUser', 'User'],
	Manager: ['Staff', 'InvoicedUser', 'FixedPriceUser', 'User'],
	Staff: [],
	InvoicedUser: [],
	FixedPriceUser: [],
	User: [],
	Guest: [],
};
